/**
 * Minimal `Zone` global shim for ZONELESS mode (issue #51).
 *
 * `@angular/fire` v16 compat schedulers (`ɵAngularFireSchedulers` / `ɵZoneScheduler`)
 * capture `Zone.current` in their constructor and later call `.run()`, `.runGuarded()`
 * and `.scheduleMacroTask()` on it. With zone.js removed for zoneless change detection,
 * the global `Zone` no longer exists, so those calls throw
 *   `ReferenceError: Zone is not defined`
 * and the whole app fails to bootstrap.
 *
 * In a zoneless app we do NOT need real Zone semantics — change detection is driven by
 * signals / AsyncPipe / events, not zone ticks. So this provides an inert `Zone.current`
 * that runs callbacks synchronously and returns no-op macrotasks. It must load before
 * any `@angular/fire` code runs, so it is imported first from `polyfills.ts`.
 */
(function installZonelessZoneShim() {
  const g: any = globalThis as any;
  if (g.Zone) {
    return; // real zone.js (or a shim) already present
  }

  const runFn = <T>(fn: (...args: any[]) => T, applyThis?: any, applyArgs?: any[]): T =>
    fn.apply(applyThis, applyArgs || []);

  const makeTask = (source: string, callback: (() => any) | undefined) => ({
    source,
    state: 'scheduled',
    get zone() {
      return zone;
    },
    invoke() {
      this.state = 'notScheduled';
      try {
        return typeof callback === 'function' ? callback() : undefined;
      } catch {
        return undefined;
      }
    },
    cancelScheduleRequest() {
      this.state = 'notScheduled';
    },
  });

  const zone: any = {
    name: 'zoneless-shim',
    parent: null,
    run: runFn,
    runGuarded: runFn,
    runTask(task: any): any {
      return task && typeof task.callback === 'function' ? task.callback() : undefined;
    },
    fork(): any {
      return zone;
    },
    wrap<T extends Function>(fn: T): T {
      return fn;
    },
    get(): any {
      return undefined;
    },
    getZoneWith(): any {
      return null;
    },
    has(): boolean {
      return false;
    },
    scheduleTask(task: any): any {
      return task;
    },
    scheduleMacroTask(source: string, callback: () => any) {
      return makeTask(source, callback);
    },
    scheduleMicroTask(source: string, callback: () => any) {
      return makeTask(source, callback);
    },
    scheduleEventTask(source: string, callback: () => any) {
      return makeTask(source, callback);
    },
    cancelTask(task: any): any {
      if (task) {
        task.state = 'notScheduled';
      }
      return task;
    },
  };
  zone.current = zone;

  g.Zone = {
    current: zone,
    root: zone,
    __symbol__: (name: string) => '__zone_symbol__' + name,
  };
})();
